import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  MenuItem,
  Modal,
  Select,
  TablePagination,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  footerStyle,
  headerStyle,
  inputStyle,
  modalStyle,
  tabStyle,
} from "../OrderEntry/styles";
import { tokens } from "../../Common/designTokens";
import MAWatchlistTableSection from "./MAWatchlistTableSection";
import MAWatchlistConfirmModals from "./MAWatchlistConfirmModals";
import MAWatchlistFilterSection from "./MAWatchlistFilterSection";
import {
  MA_STATIC_WATCHLIST_CUSIP_LIMIT,
  MA_STATIC_WATCHLIST_LIMIT_MESSAGE,
  MA_DYNAMIC_WATCHLIST_LIMIT_MESSAGE,
} from "../../Constants/AppConstants";
import {
  CREATE_WATCHLIST_ID,
  WATCHLIST_TABS,
  WATCHLIST_TYPE,
  useMAWatchlistPopupState,
} from "./useMAWatchlistPopupState";
import { getWatchlistStatusIndicatorTone } from "../../Utils/maWatchlistStatusUtils";

const WATCHLIST_POPUP_WIDTH = "1300px";
const WATCHLIST_SEARCH_HEIGHT = 36;
const WATCHLIST_NAME_MAX_LENGTH = 30;
const WATCHLIST_DROPDOWN_MAX_HEIGHT = 320;

const WATCHLIST_STATUS_DOT_SX = {
  width: 10,
  height: 10,
  borderRadius: "50%",
  flexShrink: 0,
};

const getWatchlistStatusDotColor = (watchlist) => {
  const tone = getWatchlistStatusIndicatorTone(watchlist);

  if (tone === "processing") {
    return "#f59e0b";
  }

  if (tone === "failed") {
    return "#ef4444";
  }

  return "";
};

const WATCHLIST_TAB_SX = {
  ...tabStyle,
  minWidth: "auto",
  minHeight: WATCHLIST_SEARCH_HEIGHT,
  padding: "6px 12px",
  fontSize: "0.95rem",
};

const WATCHLIST_TAB_COUNT_LOADER_SX = {
  ml: 0.25,
  color: tokens.palette.primary,
};

const WATCHLIST_SEARCH_INPUT_SX = {
  ...inputStyle,
  "& .MuiFilledInput-root": {
    ...inputStyle["& .MuiFilledInput-root"],
    backgroundColor: "transparent",
    border: "none",
    borderRadius: 0,
    height: WATCHLIST_SEARCH_HEIGHT,
    minHeight: WATCHLIST_SEARCH_HEIGHT,
    paddingTop: "2px",
    "&:hover": {
      backgroundColor: "transparent",
      border: "none",
    },
    "&:focus-within": {
      backgroundColor: "transparent",
    },
  },
  "& .MuiInputLabel-root": {
    display: "none",
  },
};

const MAWatchlistPopup = ({
  open,
  onClose,
  token,
  onError,
  onSuccess,
  onInfo,
  initialWatchlistId = null,
  createMode = false,
}) => {
  const {
    modalHeight,
    watchlists,
    activeWatchlistId,
    watchlistName,
    setWatchlistName,
    handleWatchlistSelect,
    handleCreateWatchlistDraft,
    isCreateWatchlistDraft,
    isInitializing,
    isFirstFetchStarted,
    currentWatchlist,
    loading,
    secMasterLoading,
    saving,
    error,
    secMasterError,
    watchlistItemsError,
    clearError,
    clearSecMasterError,
    clearWatchlistItemsError,
    secMasterPagination,
    itemsPagination,
    activeTab,
    setActiveTab,
    watchlistType,
    setWatchlistType,
    isDynamicWatchlist,
    isReadOnlyBackendDynamicWatchlistView,
    isProcessingWatchlistLocked,
    isFailedWatchlist,
    canRetryFailedDynamicWatchlist,
    processingWatchlistMessage,
    failedWatchlistMessage,
    bondFilter,
    handleBondFilterChange,
    handleBondFilterPaste,
    allSortConfig,
    mySortConfig,
    paginatedVisibleBonds,
    totalVisibleBonds,
    hasLoadedAllTabCount,
    hasLoadedMyTabCount,
    emptyMessage,
    myWatchlistAddedCount,
    myWatchlistRemovedCount,
    hasMyWatchlistRows,
    pendingAddedCount,
    isDeleteAllConfirmOpen,
    isCloseConfirmOpen,
    isDynamicToStaticConfirmOpen,
    isDynamicSaveCriteriaWarningOpen,
    isDynamicNoCriteriaErrorOpen,
    hasAppliedDynamicCriteria,
    isExceedingDynamicLimit,
    clearingWatchlist,
    isApplyCriteriaDisabled,
    isClearFiltersDisabled,
    rowsPerPageOptions,
    allTablePage,
    myTablePage,
    allRowsPerPage,
    myRowsPerPage,
    handleTablePageChange,
    handleRowsPerPageChange,
    handleCancel,
    handleSave,
    handleRetryFailedWatchlist,
    handleModalCloseRequest,
    handleDeleteAllRequest,
    handleDismissDeleteAllConfirmation,
    handleConfirmDeleteAll,
    handleDismissCloseConfirmation,
    handleConfirmClose,
    handleDismissDynamicToStaticConfirmation,
    handleConfirmDynamicToStaticConfirmation,
    handleDismissDynamicSaveCriteriaWarning,
    handleApplyAndReviewDynamicCriteria,
    handleContinueDynamicSaveWithAppliedCriteria,
    handleDismissDynamicNoCriteriaError,
    handleSortChange,
    handleApplyCriteria,
    isAllTabBondChecked,
    handleAllTabCheckboxToggle,
    handleRemoveFromWatchlist,
    isMyTabBondDiscarded,
    getMyWatchlistActionLabel,
    forceExpandFilter,
    resetForceExpandFilter,
    filterData,
    appliedFilters,
    filterOptions,
    loadingFilterDefaults,
    filterDefaultsError,
    clearFilterDefaultsError,
    setFilterField,
    resetFilterData,
    clearFilterFieldsAndApply,
    effectiveStaticWatchlistCusipsCount,
  } = useMAWatchlistPopupState({
    open,
    onClose,
    token,
    onError,
    onSuccess,
    onInfo,
    initialWatchlistId,
    createMode,
  });

  const selectedWatchlistName = isCreateWatchlistDraft
    ? ""
    : currentWatchlist?.watchlistName || "";
  const selectableWatchlists = watchlists.filter(
    (watchlist) => watchlist?.watchlistId !== CREATE_WATCHLIST_ID
  );
  const selectedWatchlist = selectableWatchlists.find(
    (watchlist) => String(watchlist?.watchlistId) === String(activeWatchlistId ?? "")
  );
  const hasSelectableWatchlists = selectableWatchlists.length > 0;
  const isAllTabLoading = secMasterLoading || (isInitializing && !isFirstFetchStarted);
  const isMyTabLoading = loading || (isInitializing && !isFirstFetchStarted);
  const isWatchlistNameEmpty = !`${watchlistName || ""}`.trim();
  const isExceedingStaticLimit =
    !isDynamicWatchlist
    && effectiveStaticWatchlistCusipsCount > MA_STATIC_WATCHLIST_CUSIP_LIMIT;
  const isWatchlistSelectionDisabled =
    isInitializing || saving || clearingWatchlist;

  // Show both errors from their respective stores, plus filter defaults
  const displayErrors = [
    error,
    secMasterError,
    watchlistItemsError,
    filterDefaultsError
  ].filter(Boolean);

  const handleDismissError = (errMessage) => {
    if (errMessage === filterDefaultsError) {
      clearFilterDefaultsError();
    } else if (errMessage === secMasterError) {
      clearSecMasterError();
    } else if (errMessage === watchlistItemsError) {
      clearWatchlistItemsError();
    } else {
      clearError();
    }
  };

  const renderWatchlistTabLabel = ({
    title,
    count,
    hasLoadedCount,
    isLoading,
    trailingContent = null,
  }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography component="span" sx={{ fontSize: "inherit" }}>
        {!isLoading && hasLoadedCount ? `${title} (${count.toLocaleString()})` : title}
      </Typography>
      {isLoading ? (
        <CircularProgress
          size={14}
          thickness={5}
          sx={WATCHLIST_TAB_COUNT_LOADER_SX}
        />
      ) : null}
      {trailingContent}
    </Box>
  );

  return (
    <>
      <Modal
        open={open}
        disableEscapeKeyDown
        onClose={handleModalCloseRequest}
        aria-labelledby="ma-watchlist-popup-title"
      >
        <Box
          sx={{
            ...modalStyle,
            width: WATCHLIST_POPUP_WIDTH,
            height: `${modalHeight}px`,
            maxWidth: "calc(100vw - 32px)",
            maxHeight: "calc(100vh - 8px)",
            border: `1px solid ${tokens.colors.bodyBorderColor50}`,
            boxShadow: 24,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={headerStyle}>
            <Typography
              id="ma-watchlist-popup-title"
              variant="h6"
              sx={{ fontWeight: 700, color: tokens.colors.fontColour }}
            >
              Manage Watchlists
            </Typography>

            <IconButton
              onClick={handleCancel}
              size="small"
              sx={{ color: tokens.colors.fontColour }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 3, pt: "6px", display: "flex", flexDirection: "column", gap: 2, flex: 1, minHeight: 0 }}>
            {displayErrors.length > 0 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {displayErrors.map((errMsg, idx) => (
                  <Alert severity="error" key={idx} onClose={() => handleDismissError(errMsg)}>
                    {errMsg}
                  </Alert>
                ))}
              </Box>
            )}

            {isExceedingStaticLimit && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Alert severity="warning">
                  {MA_STATIC_WATCHLIST_LIMIT_MESSAGE}
                </Alert>
              </Box>
            )}

            {isExceedingDynamicLimit && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Alert severity="warning">
                  {MA_DYNAMIC_WATCHLIST_LIMIT_MESSAGE}
                </Alert>
              </Box>
            )}

            {isFailedWatchlist ? (
              <Alert
                severity="error"
                action={
                  canRetryFailedDynamicWatchlist ? (
                    <Button
                      color="error"
                      size="small"
                      onClick={handleRetryFailedWatchlist}
                      disabled={saving || clearingWatchlist}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        minWidth: 72,
                      }}
                    >
                      {saving ? "Retrying..." : "Retry"}
                    </Button>
                  ) : null
                }
                sx={{
                  alignItems: "center",
                  "& .MuiAlert-action": {
                    alignItems: "center",
                    mr: 0,
                    pl: 2,
                  },
                }}
              >
                {failedWatchlistMessage}
              </Alert>
            ) : null}

            {/* Row 1: Watchlist dropdown + Watchlist name */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <FormControl
                sx={{
                  width: 240,
                  flexShrink: 0,
                  cursor: isWatchlistSelectionDisabled ? "not-allowed" : "default",
                }}
              >
                <Select
                  value={activeWatchlistId ?? ""}
                  displayEmpty
                  disabled={isWatchlistSelectionDisabled}
                  sx={{
                    height: WATCHLIST_SEARCH_HEIGHT,
                    width: "100%",
                    backgroundColor: tokens.colors.defaultGridRowColor,
                    color: tokens.colors.fontColour,
                    borderRadius: "8px",
                    "& .MuiSelect-select": {
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      pl: 0.5,
                      pr: 4,
                    },
                    "&.Mui-disabled": {
                      pointerEvents: "auto",
                      cursor: "not-allowed",
                      "& .MuiSelect-select": {
                        cursor: "not-allowed",
                      },
                      "& .MuiSvgIcon-root": {
                        cursor: "not-allowed",
                      }
                    },
                  }}
                  onChange={(event) => {
                    const nextWatchlistId = event.target.value;

                    if (nextWatchlistId === CREATE_WATCHLIST_ID) {
                      handleCreateWatchlistDraft();
                      return;
                    }

                    handleWatchlistSelect(nextWatchlistId);
                  }}
                  renderValue={() => {
                    if (!selectedWatchlistName) {
                      return isCreateWatchlistDraft ? "Select" : "Watchlists";
                    }

                    const selectedWatchlistDotColor = getWatchlistStatusDotColor(selectedWatchlist);

                    return (
                      <Box
                        title={selectedWatchlistName}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          minWidth: 0,
                        }}
                      >
                        {selectedWatchlistDotColor ? (
                          <Box
                            component="span"
                            sx={{
                              ...WATCHLIST_STATUS_DOT_SX,
                              backgroundColor: selectedWatchlistDotColor,
                            }}
                          />
                        ) : null}
                        <Box
                          component="span"
                          sx={{
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            minWidth: 0,
                          }}
                        >
                          {selectedWatchlistName}
                        </Box>
                      </Box>
                    );
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        width: 240,
                        maxHeight: WATCHLIST_DROPDOWN_MAX_HEIGHT,
                        overflow: "hidden",
                      },
                    },
                    MenuListProps: {
                      sx: {
                        py: 0,
                        maxHeight: WATCHLIST_DROPDOWN_MAX_HEIGHT,
                        overflowY: "auto",
                      },
                    },
                  }}
                >
                  {!hasSelectableWatchlists ? (
                    <MenuItem disabled value="__no_watchlists__">
                      No watchlists available
                    </MenuItem>
                  ) : (
                    selectableWatchlists.map((watchlist, index) => {
                      const watchlistDotColor = getWatchlistStatusDotColor(watchlist);

                      return (
                        <MenuItem
                          key={watchlist.watchlistId}
                          value={watchlist.watchlistId}
                          sx={{
                            borderBottom:
                              index < selectableWatchlists.length - 1
                                ? `1px solid ${tokens.colors.bodyBorderColor50}`
                                : "none",
                          }}
                        >
                          <Box
                            title={watchlist.watchlistName}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              minWidth: 0,
                              width: "100%",
                            }}
                          >
                            {watchlistDotColor ? (
                              <Box
                                component="span"
                                sx={{
                                  ...WATCHLIST_STATUS_DOT_SX,
                                  backgroundColor: watchlistDotColor,
                                }}
                              />
                            ) : null}
                            <Box
                              component="span"
                              sx={{
                                display: "block",
                                minWidth: 0,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {watchlist.watchlistName}
                            </Box>
                          </Box>
                        </MenuItem>
                      );
                    })
                  )}

                  <MenuItem
                    value={CREATE_WATCHLIST_ID}
                    sx={{
                      display: "flex",
                      gap: 1,
                      fontWeight: 700,
                      position: "sticky",
                      bottom: 0,
                      zIndex: 2,
                      borderTop: `1px solid ${tokens.colors.bodyBorderColor50}`,
                      bgcolor: `${tokens.colors.bodyBackgroundColor} !important`,
                      "&:hover": {
                        bgcolor: `${tokens.colors.defaultGridHoverColor} !important`,
                      },
                      "&.Mui-selected": {
                        bgcolor: `${tokens.colors.bodyBackgroundColor} !important`,
                      },
                    }}
                  >
                    <AddIcon fontSize="small" />
                    Create Watchlist
                  </MenuItem>
                </Select>
              </FormControl>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                  fullWidth
                  variant="filled"
                  label="Watchlist Name"
                  placeholder="Enter Watchlist Name"
                  value={watchlistName}
                  onChange={(e) => setWatchlistName(e.target.value.slice(0, WATCHLIST_NAME_MAX_LENGTH))}
                  disabled={isProcessingWatchlistLocked || saving || clearingWatchlist}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    disableUnderline: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography
                          component="span"
                          sx={{
                            fontSize: "0.7rem",
                            lineHeight: 1,
                            color: tokens.colors.fontColour85,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {`${watchlistName.length}/${WATCHLIST_NAME_MAX_LENGTH}`}
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{ maxLength: WATCHLIST_NAME_MAX_LENGTH }}
                  sx={{
                    ...inputStyle,
                    "& .MuiFilledInput-root": {
                      ...inputStyle["& .MuiFilledInput-root"],
                      height: `${WATCHLIST_SEARCH_HEIGHT}px`,
                      minHeight: `${WATCHLIST_SEARCH_HEIGHT}px`,
                      paddingTop: "0px",
                      boxSizing: "border-box",
                      display: "flex",
                      alignItems: "center",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.74rem",
                      transform: "translate(12px, 2px) scale(0.75)",
                      transformOrigin: "top left",
                    },
                    "& .MuiInputBase-input": {
                      paddingTop: "11px !important",
                      paddingBottom: "1px !important",
                      fontSize: "0.95rem",
                    },
                  }}
                />
              </Box>

              <ToggleButtonGroup
                exclusive
                value={watchlistType}
                onChange={(_, val) => val && setWatchlistType(val)}
                size="small"
                disabled={isProcessingWatchlistLocked || saving || clearingWatchlist}
                sx={{
                  flexShrink: 0,
                  borderRadius: "8px",
                  overflow: "hidden",
                  "& .MuiToggleButtonGroup-grouped": {
                    borderTopLeftRadius: "0 !important",
                    borderTopRightRadius: "0 !important",
                    borderBottomLeftRadius: "0 !important",
                    borderBottomRightRadius: "0 !important",
                    border: `1px solid ${tokens.colors.bodyBorderColor} !important`,
                    "&:not(:first-of-type)": {
                      borderLeft: `1px solid ${tokens.colors.bodyBorderColor} !important`,
                      marginLeft: 0,
                    },
                    "&:first-of-type": {
                      borderTopLeftRadius: "8px !important",
                      borderBottomLeftRadius: "8px !important",
                    },
                    "&:last-of-type": {
                      borderTopRightRadius: "8px !important",
                      borderBottomRightRadius: "8px !important",
                    },
                  },
                  "& .MuiToggleButton-root": {
                    textTransform: "none",
                    color: tokens.colors.fontColour,
                    px: 2,
                    height: WATCHLIST_SEARCH_HEIGHT,
                    fontWeight: 600,
                    fontSize: "0.9rem",
                  },
                  "& .MuiToggleButton-root.Mui-selected": {
                    color: tokens.palette.primary,
                    backgroundColor: `${tokens.palette.primaryA10} !important`,
                    borderColor: `${tokens.palette.primary} !important`,
                    zIndex: 2,
                  },
                  "& .MuiToggleButton-root.Mui-selected + .MuiToggleButton-root": {
                    borderLeftColor: `${tokens.palette.primary} !important`,
                  },
                }}
              >
                <ToggleButton value={WATCHLIST_TYPE.STATIC}>Static</ToggleButton>
                <ToggleButton value={WATCHLIST_TYPE.DYNAMIC}>Dynamic</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <MAWatchlistFilterSection
              filterData={filterData}
              appliedFilters={appliedFilters}
              filterOptions={filterOptions}
              onFilterChange={setFilterField}
              onClearFilters={resetFilterData}
              onClearFilterFields={clearFilterFieldsAndApply}
              disabled={
                isProcessingWatchlistLocked
                || saving
                || clearingWatchlist
                || loadingFilterDefaults
              }
              clearDisabled={isProcessingWatchlistLocked || isClearFiltersDisabled}
              applyDisabled={isProcessingWatchlistLocked || isApplyCriteriaDisabled}
              onApplyFilters={handleApplyCriteria}
              heading={isDynamicWatchlist ? "Characteristics" : "Filters"}
              isBondTypeRequired={false}
              forceExpanded={forceExpandFilter}
              onForceExpandedResponded={resetForceExpandFilter}
            />

            {/* Row 2: Tabs + Search filter */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Tabs
                value={activeTab}
                onChange={(_, newValue) => setActiveTab(newValue)}
                aria-label="watchlist tabs"
                sx={{
                  pl: "15px",
                  pr: "20px",
                  minHeight: WATCHLIST_SEARCH_HEIGHT,
                  borderBottom: `1px solid ${tokens.colors.bodyBorderColor50}`,
                  flexShrink: 0,
                  "& .MuiTab-root.Mui-selected": {
                    borderRadius: 0,
                    backgroundColor: "transparent !important",
                    borderBottom: `1px solid ${tokens.palette.primary} !important`,
                    opacity: 1,
                  },
                  "& .MuiTabs-flexContainer": {
                    alignItems: "center",
                  },
                  "& .MuiTabs-indicator": {
                    display: "none",
                  },
                }}
              >
                <Tab
                  label={renderWatchlistTabLabel({
                    title: "All",
                    count: secMasterPagination.totalElements,
                    hasLoadedCount: hasLoadedAllTabCount,
                    isLoading: isAllTabLoading,
                    trailingContent: myWatchlistAddedCount > 0 ? (
                      <Box
                        aria-label={`added cusips ${myWatchlistAddedCount}`}
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.75,
                          minWidth: 30,
                          height: 22,
                          px: 0.75,
                          borderRadius: "999px",
                          border: `1px solid ${tokens.colors.buyBgColor}`,
                          backgroundColor: "rgba(69, 181, 110, 0.15)",
                          color: tokens.colors.buyBgColor,
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            backgroundColor: tokens.colors.buyBgColor,
                          }}
                        />
                        <Typography component="span" sx={{ fontSize: "0.82rem", lineHeight: 1, fontWeight: 700 }}>
                          {myWatchlistAddedCount}
                        </Typography>
                      </Box>
                    ) : null,
                  })}
                  sx={WATCHLIST_TAB_SX}
                />
                <Tab
                  label={renderWatchlistTabLabel({
                    title: "My Watchlist",
                    count: itemsPagination.totalElements,
                    hasLoadedCount: hasLoadedMyTabCount,
                    isLoading: isMyTabLoading,
                    trailingContent: (isReadOnlyBackendDynamicWatchlistView || myWatchlistRemovedCount > 0) ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        {isReadOnlyBackendDynamicWatchlistView && (
                          <Tooltip title="Switching from a dynamic to a static watchlist will reset your data based on the new selection." arrow placement="top">
                            <InfoOutlinedIcon sx={{ fontSize: 16, color: tokens.palette.primary }} />
                          </Tooltip>
                        )}
                        {myWatchlistRemovedCount > 0 && (
                          <Box
                            aria-label={`removed cusips ${myWatchlistRemovedCount}`}
                            sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.75,
                            minWidth: 30,
                            height: 22,
                            px: 0.75,
                            borderRadius: "999px",
                            border: `1px solid ${tokens.palette.danger}`,
                            backgroundColor: "rgba(211, 47, 47, 0.12)",
                            color: tokens.palette.danger,
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              backgroundColor: tokens.palette.danger,
                            }}
                          />
                          <Typography component="span" sx={{ fontSize: "0.82rem", lineHeight: 1, fontWeight: 700 }}>
                            {myWatchlistRemovedCount}
                          </Typography>
                        </Box>
                        )}
                      </Box>
                    ) : null,
                  })}
                  sx={WATCHLIST_TAB_SX}
                />
              </Tabs>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "stretch",
                  flex: 1,
                  minWidth: 0,
                  height: WATCHLIST_SEARCH_HEIGHT,
                  border: `1px solid ${tokens.colors.bodyBorderColor50}`,
                  borderRadius: "8px",
                  overflow: "hidden",
                  backgroundColor: tokens.colors.white,
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRight: `1px solid ${tokens.colors.bodyBorderColor50}`,
                    color: tokens.colors.fontColour85,
                    flexShrink: 0,
                  }}
                >
                  <SearchIcon />
                </Box>

                <TextField
                  fullWidth
                  variant="filled"
                  value={bondFilter}
                  onChange={handleBondFilterChange}
                  onPaste={handleBondFilterPaste}
                  placeholder="Enter identifier/type/security description."
                  InputProps={{ disableUnderline: true }}
                  sx={WATCHLIST_SEARCH_INPUT_SX}
                />
              </Box>
            </Box>

            <Box
              sx={{
                border: `1px solid ${tokens.colors.bodyBorderColor50}`,
                borderRadius: "8px",
                overflow: "hidden",
                backgroundColor: tokens.colors.defaultGridRowColor,
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: 0,
              }}
            >
              <Box
                sx={{
                  backgroundColor: tokens.colors.bodyBackgroundColor,
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {isProcessingWatchlistLocked ? (
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      px: 3,
                    }}
                  >
                    <Alert severity="info" sx={{ maxWidth: 560 }}>
                      {processingWatchlistMessage}
                    </Alert>
                  </Box>
                ) : activeTab === WATCHLIST_TABS.ALL ? (
                  <MAWatchlistTableSection
                    bonds={paginatedVisibleBonds}
                    useCheckboxAction
                    isCheckboxChecked={isAllTabBondChecked}
                    isCheckboxDisabled={() =>
                      isDynamicWatchlist || isProcessingWatchlistLocked
                    }
                    onCheckboxToggle={handleAllTabCheckboxToggle}
                    emptyMessage={emptyMessage}
                    loading={isAllTabLoading}
                    loadingMessage="Loading security data..."
                    sortConfig={allSortConfig}
                    onSortChange={handleSortChange}
                    viewportHeight="100%"
                  />
                ) : (
                  <>
                    {isDynamicWatchlist || isReadOnlyBackendDynamicWatchlistView ? (
                      <Alert
                        severity="info"
                        sx={{
                          borderRadius: 0,
                          flexShrink: 0,
                          "& .MuiAlert-message": {
                            fontSize: "0.85rem",
                            fontWeight: 600,
                          },
                        }}
                      >
                        {isReadOnlyBackendDynamicWatchlistView
                          ? "Switching from a dynamic to a static watchlist will reset your data based on the new selection."
                          : "This watchlist will be reset based on the characteristics you apply. Additions and removals are handled automatically when you save."}
                      </Alert>
                    ) : null}
                    <MAWatchlistTableSection
                      bonds={paginatedVisibleBonds}
                      actionLabel="Remove"
                      useIconAction
                      onActionClick={handleRemoveFromWatchlist}
                      isActionDisabled={() =>
                        isDynamicWatchlist
                        || isReadOnlyBackendDynamicWatchlistView
                        || isProcessingWatchlistLocked
                      }
                      isDiscarded={isMyTabBondDiscarded}
                      getActionLabel={getMyWatchlistActionLabel}
                      emptyMessage={emptyMessage}
                      loading={isMyTabLoading}
                      loadingMessage="Loading watchlist..."
                      sortConfig={mySortConfig}
                      descriptionSortKey="securityDesc"
                      onSortChange={handleSortChange}
                      viewportHeight="100%"
                    />
                  </>
                )}
              </Box>

              {!isProcessingWatchlistLocked ? (
                <Box
                  sx={{
                    borderTop: `1px solid ${tokens.colors.bodyBorderColor50}`,
                    display: "flex",
                    justifyContent: "stretch",
                    px: 1,
                    backgroundColor: tokens.colors.bodyBackgroundColor,
                    position: "relative",
                    zIndex: 2,
                  }}
                >
                  <TablePagination
                    component="div"
                    sx={{
                      width: "100%",
                      backgroundColor: tokens.colors.bodyBackgroundColor,
                      ".MuiSelect-select": { p: "2px 24px 2px 8px !important" },
                      "& .MuiTablePagination-toolbar": {
                        minHeight: 40,
                        color: tokens.colors.fontColour85,
                        justifyContent: "space-between",
                        width: "100%",
                        backgroundColor: tokens.colors.bodyBackgroundColor,
                        px: 1,
                      },
                      "& .MuiTablePagination-displayedRows, & .MuiTablePagination-selectLabel": {
                        marginTop: 0,
                        marginBottom: 0,
                        fontSize: "0.8rem",
                      },
                      "& .MuiTablePagination-actions": {
                        marginLeft: 0,
                      },
                      "& .MuiTablePagination-spacer": {
                        flex: "1 1 auto",
                      },
                    }}
                    count={totalVisibleBonds}
                    page={activeTab === WATCHLIST_TABS.ALL ? allTablePage : myTablePage}
                    onPageChange={handleTablePageChange}
                    rowsPerPage={
                      activeTab === WATCHLIST_TABS.ALL ? allRowsPerPage : myRowsPerPage
                    }
                    onRowsPerPageChange={handleRowsPerPageChange}
                    rowsPerPageOptions={rowsPerPageOptions}
                    showFirstButton
                    showLastButton
                    labelDisplayedRows={({ from, to, count }) => `${from.toLocaleString()}–${to.toLocaleString()} of ${count.toLocaleString()}`}
                  />
                </Box>
              ) : null}
            </Box>

            <Box
              sx={{
                ...footerStyle,
                mt: 0,
                px: 0,
                py: 0,
                borderTop: "none",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Button
                variant="outlined"
                onClick={handleDeleteAllRequest}
                disabled={
                  isProcessingWatchlistLocked
                    ? true
                    : isDynamicWatchlist
                    ? true
                    : activeTab === WATCHLIST_TABS.MY_WATCHLIST
                    ? isReadOnlyBackendDynamicWatchlistView
                      || !currentWatchlist?.watchlistId
                      || !hasMyWatchlistRows
                      || saving
                      || clearingWatchlist
                    : pendingAddedCount === 0 || saving || clearingWatchlist
                }
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  px: 3,
                  fontWeight: 700,
                  color:
                    activeTab === WATCHLIST_TABS.MY_WATCHLIST
                      ? tokens.palette.danger
                      : undefined,
                  borderColor:
                    activeTab === WATCHLIST_TABS.MY_WATCHLIST
                      ? tokens.palette.danger
                      : undefined,
                  "&:hover": {
                    borderColor:
                      activeTab === WATCHLIST_TABS.MY_WATCHLIST
                        ? tokens.palette.danger
                        : undefined,
                    backgroundColor:
                      activeTab === WATCHLIST_TABS.MY_WATCHLIST
                        ? "rgba(211, 47, 47, 0.08)"
                        : undefined,
                  },
                }}
              >
                {activeTab === WATCHLIST_TABS.MY_WATCHLIST ? "Delete All" : "Clear All"}
              </Button>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  disabled={saving || clearingWatchlist}
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    px: 3,
                    fontWeight: 700,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={
                    isProcessingWatchlistLocked
                    || loading
                    || saving
                    || clearingWatchlist
                    || isWatchlistNameEmpty
                    || isExceedingStaticLimit
                    || isExceedingDynamicLimit
                  }
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    px: 3,
                    fontWeight: 700,
                    backgroundColor: tokens.palette.primary,
                    color: tokens.colors.white,
                    "&:hover": {
                      backgroundColor: tokens.palette.primaryA80,
                    },
                    "&.Mui-disabled": {
                      backgroundColor: tokens.colors.disabledBackground,
                      color: tokens.colors.disabledFontColour,
                      opacity: 1,
                    },
                  }}
                >
                  {saving ? "Saving..." : "Save"}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Modal>

      <MAWatchlistConfirmModals
        isDeleteAllConfirmOpen={isDeleteAllConfirmOpen}
        onDismissDeleteAllConfirmation={handleDismissDeleteAllConfirmation}
        clearingWatchlist={clearingWatchlist}
        onConfirmDeleteAll={handleConfirmDeleteAll}
        isCloseConfirmOpen={isCloseConfirmOpen}
        onDismissCloseConfirmation={handleDismissCloseConfirmation}
        saving={saving}
        onConfirmClose={handleConfirmClose}
        isDynamicToStaticConfirmOpen={isDynamicToStaticConfirmOpen}
        onDismissDynamicToStaticConfirmation={handleDismissDynamicToStaticConfirmation}
        onConfirmDynamicToStaticConfirmation={handleConfirmDynamicToStaticConfirmation}
        isDynamicSaveCriteriaWarningOpen={isDynamicSaveCriteriaWarningOpen}
        isDynamicNoCriteriaErrorOpen={isDynamicNoCriteriaErrorOpen}
        hasAppliedDynamicCriteria={hasAppliedDynamicCriteria}
        onDismissDynamicSaveCriteriaWarning={handleDismissDynamicSaveCriteriaWarning}
        onApplyAndReviewDynamicCriteria={handleApplyAndReviewDynamicCriteria}
        onContinueDynamicSaveWithAppliedCriteria={handleContinueDynamicSaveWithAppliedCriteria}
        onDismissDynamicNoCriteriaError={handleDismissDynamicNoCriteriaError}
      />
    </>
  );
};

export default MAWatchlistPopup;
